import React, { useEffect, useState } from 'react';
import { CssVariable, VariableComponent, VariableGroup } from '../../../types';
import { VariableInput } from './inputBuilder';
import { Colors, Typography } from 'packages/radiant-react/typography';
import { Vertical } from 'packages/radiant-react/layout';
import styles from '../styles.module.scss';
import { getGroupedVariables, getBasicGroupedVariables, sortOrders } from '../../../utils';

// Constant
const ADVANCED_SECTION = {
  id: 999,
  title: 'Advanced',
  isHidden: false,
  component: (
    <Typography variant="section-label" className={styles.advancedTitle}>
      {'Advanced'.toUpperCase()}
    </Typography>
  ),
};

interface VariableSectionProps {
  variables: {
    name: string;
    variables: CssVariable[];
  }[];
}

export const VariableSection = (props: VariableSectionProps) => {
  return (
    <Vertical className={styles.sectionWrapper}>
      {props.variables.map((subGroup, index) => (
        <React.Fragment key={subGroup.name || `subgroup-${index}`}>
          {subGroup.name && (
            <Typography variant="p" color={Colors.grayLight} className={styles.sectionTitle}>
              {subGroup.name.toUpperCase()}
            </Typography>
          )}
          <Vertical
            className={subGroup.name ? styles.sectionContent : styles.sectionContentNoTitle}
          >
            {subGroup.variables.map((variable) => (
              <VariableInput key={variable.key} variable={variable} />
            ))}
          </Vertical>
        </React.Fragment>
      ))}
    </Vertical>
  );
};

export const getSectionComponents = async (
  releaseVariables: any,
  component?: VariableComponent
) => {
  const groups = await getGroupedVariables(releaseVariables, component);
  const sections = groups.map((group, index) => {
    return {
      id: index + 99,
      title: group.name,
      isHidden: false,
      component: <VariableSection variables={group.subGroup} key={group.name} />,
    };
  });

  // Add Advanced section based on the sort order for the current component
  const sortOrder = sortOrders[component || VariableComponent.Default] || [];
  const advancedIndex = sortOrder.indexOf(VariableGroup.Advanced);

  if (advancedIndex !== -1) {
    // Find the group that comes before Advanced in the sort order
    const previousGroup = sortOrder[advancedIndex - 1];

    if (previousGroup) {
      const insertIndex = sections.findIndex((section) => section.title === previousGroup);
      if (insertIndex !== -1) {
        sections.splice(insertIndex + 1, 0, ADVANCED_SECTION as any);
      }
    }
  }

  return sections;
};

/**
 * Hook that loads variables progressively for optimal user experience.
 *
 * Strategy:
 * 1. Load basic variables immediately (synchronous) for instant display
 * 2. Load full variables in background (async) including feature flag variables
 * 3. Replace basic variables with full variables when ready
 *
 * This ensures users see content immediately while getting enhanced with
 * feature flag-dependent variables when authentication completes.
 *
 * @param releaseVariables - Object containing available variable keys
 * @param component - Component type to determine sort order
 * @returns Array of section components ready for rendering
 */
export const useSectionComponents = (releaseVariables: any, component?: VariableComponent) => {
  const [sections, setSections] = useState<any[]>([]);

  useEffect(() => {
    const loadSections = async () => {
      // Phase 1: Load basic variables immediately (synchronous)
      const basicData = getBasicGroupedVariables(releaseVariables, component);
      const basicSections = basicData.map((group, index) => {
        return {
          id: index + 99,
          title: group.name,
          isHidden: false,
          component: <VariableSection variables={group.subGroup} key={group.name} />,
        };
      });

      // Add Advanced section based on the sort order for the current component
      const sortOrder = sortOrders[component || VariableComponent.Default] || [];
      const advancedIndex = sortOrder.indexOf(VariableGroup.Advanced);

      if (advancedIndex !== -1) {
        // Find the group that comes before Advanced in the sort order
        const previousGroup = sortOrder[advancedIndex - 1];

        if (previousGroup) {
          const insertIndex = basicSections.findIndex((section) => section.title === previousGroup);
          if (insertIndex !== -1) {
            basicSections.splice(insertIndex + 1, 0, ADVANCED_SECTION as any);
          }
        }
      }

      // Show basic variables immediately for instant user feedback
      setSections(basicSections);

      // Phase 2: Load full variables in the background (async)
      try {
        const fullData = await getGroupedVariables(releaseVariables, component);
        const fullSections = fullData.map((group, index) => {
          return {
            id: index + 99,
            title: group.name,
            isHidden: false,
            component: <VariableSection variables={group.subGroup} key={group.name} />,
          };
        });

        // Add Advanced section to full data
        if (advancedIndex !== -1) {
          const previousGroup = sortOrder[advancedIndex - 1];
          if (previousGroup) {
            const insertIndex = fullSections.findIndex(
              (section) => section.title === previousGroup
            );
            if (insertIndex !== -1) {
              fullSections.splice(insertIndex + 1, 0, ADVANCED_SECTION);
            }
          }
        }

        // Replace basic variables with full variables (includes feature flag variables)
        setSections(fullSections);
      } catch (error) {
        console.warn('Failed to load full variables, keeping basic variables:', error);
        // Graceful fallback: keep showing basic variables if full loading fails
      }
    };

    loadSections();
  }, [component, releaseVariables]);

  return sections;
};
